import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { Gizmo } from '../../gizmos';
import { IBinderSetDataParams, IError, IGizmo, IGizmoData } from '../../types';
import CommonBinder from '../common-binder';

interface INumberFieldBinder {
  init(): Promise<void>;
  setValue(value: any): void;
  getValue(): number | undefined;
  value: Subject<number>;
}

class NumberFieldBinder extends CommonBinder implements INumberFieldBinder {
  public value: Subject<number>;
  /**
   * This variable will be used in order store the value that's coming from the platform
   * layer, this is done because this string could have the a decimal point that has
   * to be onthe masked value
   */
  private _originalValue?: string;
  /**
   * This is the value that's going to be sent to the platform layer to be displayed on
   * the input
   */
  private _maskedValue: string;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.value = new Subject<number>();
    this._maskedValue = '';

    this._gizmo.data.subscribe(
      (data: IGizmoData | IGizmoData[] | undefined) => {
        if (data && !Array.isArray(data) && typeof data.v === 'number') {
          this._setMaskedValue(data.v);
          this.value.next(data.v);
        } else if (data === undefined) {
          this._maskedValue = '';
          this.value.next(0);
        }
      }
    );
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const data = this._gizmo.getData();
      const config: IGizmo = this._gizmo.getConfig();

      if (data && !Array.isArray(data) && typeof data.v === 'number') {
        this._setMaskedValue(data.v);
      } else if (config.ops?.value?.flux && !data) {
        const flux = this._gizmo.getFlux();

        if (flux) {
          let conditionResult = true;

          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });

            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (conditionResult === true) {
            const result = await flux.evaluate({
              script: config.ops.value.flux,
            });

            if (result !== undefined && typeof result === 'number') {
              await this.setValue(result);
            }
          }
        }
      }

      resolve();
    });
  };

  /**
   * Set an answer
   * @param {any} value value being set by the UI layer, it's a string because the inputs
   * return a string
   */
  public setValue = (value?: any): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config: IGizmo = this._gizmo.getConfig();

      if (value === undefined) {
        this._gizmo.setData(undefined);
        return resolve();
      }

      if (value !== undefined && typeof value === 'string') {
        value = value.replace(/,/g, '');
      } else if (value !== undefined && typeof value === 'number') {
        value = value.toString();
      }
      this._originalValue = value;

      if (value || value === 0) {
        if (config.ops?.numberField?.decimals) {
          // Default decimals are two
          let numberOfDecimals = 2;

          if (
            config.ops.numberField.numberOfDecimals &&
            config.ops.numberField.numberOfDecimals > 0
          ) {
            numberOfDecimals = config.ops.numberField.numberOfDecimals;
          }

          let [integral, decimals] = value.split('.');

          if (decimals) {
            if (decimals.length > numberOfDecimals) {
              decimals = decimals.substring(0, numberOfDecimals);
            }
            value = `${integral}.${decimals}`;
          } else {
            value = `${integral}`;
          }
        } else {
          const [integral] = value.split('.');
          value = integral;
        }

        this._gizmo.setData({ v: Number(value) });
      } else {
        this._gizmo.setData();
      }

      resolve();
    });
  };

  /**
   * Get value as a number
   *
   * @returns {number} number value
   */
  public getValue = (): number | undefined => {
    const data = this._gizmo.getData() as IGizmoData;

    if (data?.v !== undefined && typeof data.v === 'number') {
      return data.v;
    }

    return undefined;
  };

  /**
   * Set data to the gizmo
   * @param data gizmo data to be set
   */
  public setData = async ({ data }: IBinderSetDataParams) => {
    if (data.v !== undefined && typeof data.v === 'number') {
      this.setValue(data.v);
    }
  };

  /**
   * Set the value that will be used on the platform layer
   *
   * @param {number} value value cominfg from the gizmo data
   */
  private _setMaskedValue = (value: number) => {
    const config: IGizmo = this._gizmo.getConfig();

    if (config.ops?.numberField?.thousands) {
      const [integral, decimals] = value.toString().split('.');
      let integralWithThousands = integral;

      if (config.ops.numberField.thousands) {
        integralWithThousands = integral.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }

      if (config.ops.numberField.decimals) {
        if (decimals === undefined) {
          this._maskedValue = `${integralWithThousands}`;
          if (this._originalValue) {
            this._maskedValue += this._originalValue.includes('.') ? '.' : '';
          }
        } else {
          this._maskedValue = `${integralWithThousands}.${decimals}`;
        }
      } else {
        this._maskedValue = `${integralWithThousands}`;
      }
    } else {
      this._maskedValue = value.toString();

      if (
        config.ops?.numberField?.decimals &&
        this._originalValue &&
        !this._maskedValue.includes('.')
      ) {
        this._maskedValue += this._originalValue.includes('.') ? '.' : '';
      }
    }
  };

  /**
   * Get the masked value
   *
   * @returns masked value
   */
  public getMaskedValue = (): string | undefined => {
    return this._maskedValue;
  };

  /**
   * Send a special key to the core
   *
   * @param {string} key special key being sent
   * @returns
   */
  public sendSpecialKey = (key: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      if (key === 'enter') {
        // Right now we're only supporting enter key
        await this._gizmo.dispatchGizmoOperation({
          operation: 'keyboard.key',
          params: {
            key,
          },
        });
      }

      resolve();
    });
  };

  /**
   * Binder specific validation
   *
   * @returns {Promise<IError[]>}
   */
  public binderValidate = (): Promise<IError[]> => {
    return new Promise<IError[]>(async resolve => {
      const errors: IError[] = [];
      const config = this._gizmo.getConfig();
      const value = this.getValue();

      if (
        config.ops?.numberField?.minimum !== undefined &&
        value !== undefined &&
        value < config.ops.numberField.minimum
      ) {
        errors.push({
          fid: uuid(),
          code: 'number_field.minimum',
          message: `El valor mínimo es: ${config.ops.numberField.minimum}`,
        });
      }

      if (
        config.ops?.numberField?.maximum !== undefined &&
        value !== undefined &&
        value > config.ops.numberField.maximum
      ) {
        errors.push({
          fid: uuid(),
          code: 'number_field.maximum',
          message: `El valor máximo es: ${config.ops.numberField.maximum}`,
        });
      }

      if (
        config.ops?.numberField?.negative !== true &&
        value !== undefined &&
        value < 0
      ) {
        errors.push({
          fid: uuid(),
          code: 'number_field.negative',
          message: 'El valor no puede ser negativo',
        });
      }

      resolve(errors);
    });
  };
}

export default NumberFieldBinder;
